import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card,
    Input,
    Button,
    Progress,
    Typography,
    Space,
    message,
    Spin,
    Result,
    Alert,
    Divider,
    Modal,
    Statistic,
    Tag,
    Row,
    Col,
    Select,
    Switch
} from 'antd';
import {
    LeftOutlined,
    RightOutlined,
    CheckOutlined,
    AppstoreOutlined,
    ClockCircleOutlined,
    WarningOutlined
} from '@ant-design/icons';
import { getModuleAssessmentSubjective, submitAssessmentSubjective, getTrainingModules, gradeSingleAssessmentSubjective } from '../services/trainingService';
import { getMoodleProgramDetails } from '../services/moodleService';
import dayjs from 'dayjs';
import { parseModuleId } from '../utils/moduleUtils';
import type { Question, Module, UserAnswer, AssessmentData } from '../types/training';
import { useFullscreen } from '../hooks/useFullscreen';
import { useVisibility } from '../hooks/useVisibility';
import { FullscreenWarningModal } from '../components/FullscreenWarningModal';

// CodeMirror imports
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { cpp } from '@codemirror/lang-cpp';
import { go } from '@codemirror/lang-go';
import { java } from '@codemirror/lang-java';
import { sql } from '@codemirror/lang-sql';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';

// C# support if available via legacy or specialized package
// For now we'll use a placeholder or generic if @replit isn't easily imported as a standard lang
import { StreamLanguage } from '@codemirror/language';
import { csharp } from '@replit/codemirror-lang-csharp';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const SubjectiveAssessment: React.FC = () => {
    const { trainingId } = useParams<{ trainingId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Code Editor State
    const [useCodeEditor, setUseCodeEditor] = useState<boolean>(false);
    const [editorLanguage, setEditorLanguage] = useState<string>('javascript');
    const [isLanguageFixed, setIsLanguageFixed] = useState<boolean>(false);

    // Get module from URL query parameter
    const rawModuleParams = searchParams.get('module');
    const decodeId = rawModuleParams ? decodeURIComponent(rawModuleParams) : null;
    const moduleIdFromUrl = decodeId ? decodeId.split('[')[0].split(':')[0].trim() : null;
    const quizIdFromUrl = parseInt(searchParams.get('quizId') || '0', 10);

    // Module selection state
    const [modules, setModules] = useState<Module[]>([]);
    const [selectedModule, setSelectedModule] = useState<string | null>(null);
    const [pendingModuleId, setPendingModuleId] = useState<string | null>(moduleIdFromUrl);
    const [loadingModules, setLoadingModules] = useState<boolean>(true);
    const [programBlocked, setProgramBlocked] = useState<string | null>(null);

    // Assessment state
    const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<number>(0);
    const [answers, setAnswers] = useState<Map<any, string>>(new Map());
    const [gradedAnswers, setGradedAnswers] = useState<Map<string, { text: string, score: number }>>(new Map());
    const [gradingInProgress, setGradingInProgress] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [completed, setCompleted] = useState<boolean>(false);
    const [result, setResult] = useState<any>(null);
    const [activeQuizId, setActiveQuizId] = useState<number>(quizIdFromUrl);

    // Timer state
    const ASSESSMENT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds
    const [timeRemaining, setTimeRemaining] = useState<number>(ASSESSMENT_DURATION);
    const [timerStarted, setTimerStarted] = useState<boolean>(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const submitAnswersRef = useRef<(autoSubmit: boolean) => Promise<void>>(async () => {});

    // Fullscreen state
    const { isFullscreen, requestFullscreen, exitFullscreen, isSupported } = useFullscreen();
    const [fullscreenError, setFullscreenError] = useState<string | null>(null);
    // Visibility and Focus state
    const { isVisible, isFocused } = useVisibility();
    const [requestingFullscreen, setRequestingFullscreen] = useState<boolean>(false);
    const [fullscreenAcknowledged, setFullscreenAcknowledged] = useState<boolean>(false);
    const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
    const [fullscreenViolation, setFullscreenViolation] = useState<boolean>(false);
    const [terminationReason, setTerminationReason] = useState<string | null>(null);
    const [violationReason, setViolationReason] = useState<string | null>(null);

    useEffect(() => {
        if (trainingId) {
            loadModules();
        }
    }, [trainingId]);

    // Handle initial module from URL or auto-selection
    useEffect(() => {
        if (pendingModuleId && !selectedModule && !completed && !showWarningModal && !requestingFullscreen) {
            setShowWarningModal(true);
        }
    }, [pendingModuleId, selectedModule, completed, showWarningModal, requestingFullscreen]);

    useEffect(() => {
        if (selectedModule && trainingId) {
            loadAssessment();
        }
    }, [selectedModule, trainingId]);

    // Keep submitAnswersRef pointing at the latest submitAnswers so the timer
    // interval never calls a stale closure with empty answers/gradedAnswers.
    useEffect(() => { submitAnswersRef.current = submitAnswers; });

    // Timer effect
    useEffect(() => {
        if (timerStarted && !completed && !submitting) {
            timerRef.current = setInterval(() => {
                setTimeRemaining((prev) => {
                    if (prev <= 1000) {
                        // Time's up - auto submit
                        clearInterval(timerRef.current!);
                        message.warning('Time expired - Assessment auto-submitted for AI Grading');
                        submitAnswersRef.current(true);
                        return 0;
                    }
                    return prev - 1000;
                });
            }, 1000);

            return () => {
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
            };
        }
    }, [timerStarted, completed, submitting]);

    // Prevent page navigation during assessment
    useEffect(() => {
        if (timerStarted && !completed) {
            const handleBeforeUnload = (e: BeforeUnloadEvent) => {
                e.preventDefault();
                e.returnValue = '';
            };

            window.addEventListener('beforeunload', handleBeforeUnload);

            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }
    }, [timerStarted, completed]);

    const loadModules = async (): Promise<void> => {
        if (!trainingId) return;

        try {
            setLoadingModules(true);
            const prog = await getMoodleProgramDetails(trainingId);
            if (!prog?.active) {
                setProgramBlocked('This program has been closed and is no longer accepting submissions.');
                return;
            }
            const isEvergreen = !prog.startDate && !prog.endDate;
            const isExpired = !isEvergreen && prog.endDate
                ? dayjs().isAfter(dayjs(prog.endDate.split('T')[0]).endOf('day'))
                : false;
            if (isExpired) {
                setProgramBlocked('The deadline for this program has passed and it is no longer accepting submissions.');
                return;
            }
            const data = await getTrainingModules(trainingId);
            setModules(data);

            // Guard: if a specific module was linked via URL, check it's still accessible
            if (moduleIdFromUrl || quizIdFromUrl > 0) {
                const linked = quizIdFromUrl > 0
                    ? data.find((m: any) => m.moodleInstance === quizIdFromUrl) ?? data.find((m: any) => m.moduleId === moduleIdFromUrl)
                    : data.find((m: any) => m.moduleId === moduleIdFromUrl);
                if (linked) {
                    const isPassed = (linked.status || '').toLowerCase() === 'passed';
                    const limit = linked.attemptLimit ?? 0;
                    const attempts = linked.attempts ?? 0;
                    if (isPassed) {
                        setProgramBlocked('You have already passed this assessment.');
                        setPendingModuleId(null);
                        return;
                    }
                    if (limit > 0 && attempts >= limit) {
                        setProgramBlocked(`You have reached the maximum number of attempts (${limit}) for this assessment.`);
                        setPendingModuleId(null);
                        return;
                    }
                }
            }

            // Auto-select first module if only one exists and none specified in URL
            if (data.length === 1 && !moduleIdFromUrl) {
                setPendingModuleId(data[0].moduleId);
            }
        } catch (error) {
            console.error('Error loading modules:', error);
            message.error('Failed to load training modules');
        } finally {
            setLoadingModules(false);
        }
    };

    const loadAssessment = async (): Promise<void> => {
        if (!trainingId || !selectedModule) return;

        try {
            setLoading(true);
            
            // When quizIdFromUrl is set, look up by moodleInstance to avoid picking the wrong module
            // when multiple quizzes share the same name
            const moduleInfo = quizIdFromUrl > 0
                ? modules.find(m => m.moodleInstance === quizIdFromUrl) ?? modules.find(m => m.moduleId === selectedModule)
                : modules.find(m => m.moduleId === selectedModule);
            const quizName = moduleInfo ? moduleInfo.name : selectedModule;
            const resolvedQuizId = quizIdFromUrl > 0 ? quizIdFromUrl : (moduleInfo?.moodleInstance ?? 0);
            setActiveQuizId(resolvedQuizId);

            // Call backend API (which integrates with Moodle headless secure token)
            const data = await getModuleAssessmentSubjective(trainingId, selectedModule, resolvedQuizId);
            
            if (data && data.questions && data.questions.length > 0) {
                setAssessmentData(data);
                setQuestions(data.questions);
            } else {
                throw new Error("No questions found in this assessment module.");
            }

            setAnswers(new Map());
            setGradedAnswers(new Map());
            setGradingInProgress(new Set());
            setCurrentQuestion(0);

            // Start timer — prefer timelimit from quiz data, fall back to modules API estimatedTime
            const durationMins = (data.module?.timelimit && data.module.timelimit > 0)
              ? data.module.timelimit
              : (moduleInfo?.estimatedTime || 30);
            const durationMs = durationMins * 60 * 1000;

            setTimeRemaining(durationMs);
            setTimerStarted(true);
            setFullscreenAcknowledged(true);
        } catch (error) {
            console.error('Error loading assessment:', error);
            message.error('Failed to load assessment questions from Moodle.');
        } finally {
            setLoading(false);
        }
    };

    // Effect to handle language lock and editor auto-toggle
    useEffect(() => {
        if (questions[currentQuestion]) {
            const q = questions[currentQuestion] as any;
            if (q.RequiredLanguage) {
                setEditorLanguage(q.RequiredLanguage.toLowerCase());
                setUseCodeEditor(true);
                setIsLanguageFixed(true);
            } else {
                setIsLanguageFixed(false);
                // We don't necessarily turn off the editor, 
                // but we unlock the language selection.
            }
        }
    }, [currentQuestion, questions]);

    const getLanguageExtension = (lang: string) => {
        const extensions = [];
        
        // Language specific extension
        switch (lang.toLowerCase()) {
            case 'javascript': extensions.push(javascript({ jsx: true })); break;
            case 'typescript': extensions.push(javascript({ typescript: true })); break;
            case 'python': extensions.push(python()); break;
            case 'cpp': extensions.push(cpp()); break;
            case 'csharp': extensions.push(csharp()); break;
            case 'go': extensions.push(go()); break;
            case 'java': extensions.push(java()); break;
            case 'sql': extensions.push(sql()); break;
            default: extensions.push(javascript()); break;
        }

        // Security extensions (Disable Paste)
        extensions.push(EditorView.domEventHandlers({
            paste: (event: ClipboardEvent) => {
                event.preventDefault();
                message.warning('Pasting is disabled for assessment integrity.');
                return true;
            },
            drop: (event: DragEvent) => {
                event.preventDefault();
                message.warning('Dragging content is disabled.');
                return true;
            }
        }));

        return extensions;
    };

    const handleAcknowledgeWarning = async (): Promise<void> => {
        if (!pendingModuleId) return;

        setFullscreenError(null);
        setRequestingFullscreen(true);
        setShowWarningModal(false);

        try {
            if (!isSupported) {
                Modal.warning({
                    title: 'Fullscreen Not Supported',
                    content: (
                        <Space direction="vertical" size="middle">
                            <Text>Your browser does not fully support fullscreen mode. The assessment will proceed in reduced security mode.</Text>
                        </Space>
                    ),
                    onOk: () => {
                        setSelectedModule(pendingModuleId);
                        setPendingModuleId(null);
                    },
                    okText: 'Continue Anyway',
                });
                setRequestingFullscreen(false);
                return;
            }

            const success = await requestFullscreen();

            if (success) {
                setSelectedModule(pendingModuleId);
                setPendingModuleId(null);
            } else {
                setFullscreenError('Fullscreen mode is required to take this assessment. Please allow fullscreen access and try again.');
            }
        } catch (error) {
            console.error('Error requesting fullscreen:', error);
            setFullscreenError('Unable to enter fullscreen mode. Please ensure your browser supports fullscreen and try again.');
        } finally {
            setRequestingFullscreen(false);
        }
    };

    const handleCancelWarning = (): void => {
        setShowWarningModal(false);
        setPendingModuleId(null);
        if (trainingId) {
            navigate(`/training/${encodeURIComponent(trainingId!)}`);
        } else {
            navigate('/');
        }
    };

    const terminateAssessment = useCallback(async (reason: string): Promise<void> => {
        if (!trainingId || !selectedModule || !assessmentData) return;

        console.log('Terminating assessment due to:', reason);

        try {
            setSubmitting(true);
            setTerminationReason(reason);

            if (timerRef.current) clearInterval(timerRef.current);
            const userAnswers: UserAnswer[] = questions.map(question => {
                const selectedAnswer = answers.get(question.id) || '';
                return {
                    questionId: question.id,
                    selectedAnswer,
                    isCorrect: false // AI will judge this on backend
                };
            });

            const assessmentResult = await submitAssessmentSubjective(
                trainingId,
                selectedModule,
                userAnswers,
                'fullscreen_violation',
                {
                    timestamp: new Date(),
                    reason: reason,
                    questionsAnswered: answers.size,
                    totalQuestions: questions.length
                },
                activeQuizId
            );

            setResult({
                ...assessmentResult,
                terminationReason: 'fullscreen_violation',
                fullscreenViolation: {
                    timestamp: new Date(),
                    reason: reason,
                    questionsAnswered: answers.size,
                    totalQuestions: questions.length
                }
            });

            setCompleted(true);
            setFullscreenViolation(true);

            try {
                await exitFullscreen();
            } catch (error) { }

            message.error('Assessment terminated due to fullscreen violation');
        } catch (error) {
            console.error('Error terminating assessment:', error);
            message.error('Failed to submit assessment to AI Grader');
        } finally {
            setSubmitting(false);
        }
    }, [trainingId, selectedModule, assessmentData, questions, answers, exitFullscreen]);

    const handleViolation = useCallback((reason: string) => {
        console.log(`Strict violation detected: ${reason}. Terminating assessment immediately.`);
        setViolationReason(reason); // Set this IMMEDIATELY for instant UI feedback
        terminateAssessment(reason);
    }, [terminateAssessment]);

    const handleFullscreenChange = useCallback(() => {
        // Only monitor if assessment is active and acknowledged
        const assessmentActive = fullscreenAcknowledged && !completed && !submitting && !showWarningModal;

        if (!assessmentActive) {
            console.log('Fullscreen monitoring inactive - not acknowledged yet or assessment complete');
            return;
        }

        // Detect exit from fullscreen OR window/tab blur OR hidden visibility
        const isViolation = !isFullscreen || !isVisible || !isFocused;

        // If a violation was detected during active assessment, terminate immediately
        if (isViolation) {
            let reason = 'Security Violation';
            if (!isFullscreen) reason = 'Fullscreen Exit';
            else if (!isVisible) reason = 'Tab Switch';
            else if (!isFocused) reason = 'Desktop/Window Switch';

            handleViolation(reason);
        }
    }, [isFullscreen, isVisible, isFocused, fullscreenAcknowledged, completed, submitting, showWarningModal, handleViolation]);

    // Monitor violations (fullscreen, visibility, focus) during assessment
    useEffect(() => {
        // Only start monitoring AFTER warning is acknowledged
        if (fullscreenAcknowledged && !completed && !submitting && !showWarningModal) {
            // Assessment is active, monitor for violations
            handleFullscreenChange();
        }
    }, [isFullscreen, isVisible, isFocused, fullscreenAcknowledged, completed, submitting, showWarningModal, handleFullscreenChange]);

    const handleStartAssessment = async (moduleId: string): Promise<void> => {
        setPendingModuleId(moduleId);
        setShowWarningModal(true);
    };

    const handleAnswerChange = (value: string): void => {
        if (!fullscreenAcknowledged) return;
        const questionId = questions[currentQuestion].id as any;
        setAnswers(prev => new Map(prev).set(questionId, value));
    };

    const triggerBackgroundGrading = async (questionId: string, currentText: string) => {
        if (!currentText || currentText.trim() === '') return;
        if (!trainingId) return;

        const cached = gradedAnswers.get(questionId);
        if (cached && cached.text === currentText) return; // already graded

        setGradingInProgress(prev => new Set(prev).add(questionId));

        try {
            const question = questions.find(q => q.id.toString() === questionId.toString());
            const graderInfo = question?.graderInfo || '';

            const { score } = await gradeSingleAssessmentSubjective(trainingId, selectedModule || '', questionId, currentText, graderInfo, question?.question || '', question?.codeSnippet || null, activeQuizId);
            setGradedAnswers(prev => {
                const map = new Map(prev);
                map.set(questionId, { text: currentText, score });
                return map;
            });
        } catch (error) {
            console.error('Background AI grader failed for question', questionId, error);
        } finally {
            setGradingInProgress(prev => {
                const next = new Set(prev);
                next.delete(questionId);
                return next;
            });
        }
    };

    const handleNext = (): void => {
        if (!fullscreenAcknowledged) return;

        const questionId = questions[currentQuestion].id as any;
        const currentText = answers.get(questionId) || '';
        triggerBackgroundGrading(questionId, currentText);

        if (currentQuestion < questions.length - 1) setCurrentQuestion(prev => prev + 1);
    };

    const handlePrevious = (): void => {
        if (!fullscreenAcknowledged) return;

        const questionId = questions[currentQuestion].id as any;
        const currentText = answers.get(questionId) || '';
        triggerBackgroundGrading(questionId, currentText);

        if (currentQuestion > 0) setCurrentQuestion(prev => prev - 1);
    };

    const handleSubmit = (): void => {
        if (!fullscreenAcknowledged) return;

        const unanswered = questions.filter(q => {
            const ans = answers.get(q.id);
            return !ans || ans.trim() === '';
        });

        if (unanswered.length > 0) {
            Modal.confirm({
                title: 'Incomplete Assessment',
                content: `${unanswered.length} question(s) remain unanswered. Do you wish to proceed with submission to the AI for grading? Unanswered questions will receive 0%.`,
                onOk: () => submitAnswers(false),
                okText: 'Submit to AI Grader',
                cancelText: 'Back to Review',
                okButtonProps: { danger: true }
            });
        } else {
            Modal.confirm({
                title: 'Submit Assessment',
                content: 'Your written answers will be evaluated by an AI Agent. Submissions cannot be modified after confirmation.',
                onOk: () => submitAnswers(false),
                okText: 'Send to AI for Grading',
                cancelText: 'Cancel',
                okButtonProps: { style: { backgroundColor: '#fa541c', borderColor: '#fa541c' } }
            });
        }
    };

    const submitAnswers = async (autoSubmit: boolean = false): Promise<void> => {
        if (!trainingId || !selectedModule || !assessmentData) return;

        try {
            setSubmitting(true);
            message.loading({ content: 'Finalizing assessment and missing grades...', key: 'aigrading', duration: 0 });

            if (timerRef.current) clearInterval(timerRef.current);

            const userAnswers: (UserAnswer & { aiScore?: number })[] = [];

            for (const question of questions) {
                const questionId = question.id as any;
                const selectedAnswer = answers.get(questionId) || '';
                let aiScore = 0;

                if (selectedAnswer.trim() !== '') {
                    const cached = gradedAnswers.get(questionId);
                    if (cached && cached.text === selectedAnswer) {
                        aiScore = cached.score;
                    } else {
                        // Grade instantly if user skipped Next button on the last question
                        try {
                            const graderInfo = question.graderInfo || '';
                            const { score } = await gradeSingleAssessmentSubjective(trainingId, selectedModule || '', questionId, selectedAnswer, graderInfo, question.question || '', question.codeSnippet || null, activeQuizId);
                            aiScore = score;
                            setGradedAnswers(prev => {
                                const map = new Map(prev);
                                map.set(questionId, { text: selectedAnswer, score });
                                return map;
                            });
                        } catch (error) {
                            console.error('Final grading failed for question', questionId, error);
                        }
                    }
                }

                userAnswers.push({
                    questionId: question.id,
                    selectedAnswer,
                    isCorrect: false,
                    aiScore
                });
            }

            const assessmentResult = await submitAssessmentSubjective(trainingId, selectedModule, userAnswers, undefined, undefined, activeQuizId);
            setResult(assessmentResult);
            setCompleted(true);

            message.success({ content: 'AI Grading complete!', key: 'aigrading' });

            try {
                await exitFullscreen();
            } catch (error) { }

        } catch (error) {
            console.error('Error submitting assessment:', error);
            message.error({ content: 'Failed to submit assessment for AI grading', key: 'aigrading' });
        } finally {
            setSubmitting(false);
        }
    };

    // UI Renders omitted mostly duplicated from Assessment.tsx
    if (loadingModules) return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" /></div>;

    if (programBlocked) return (
        <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
            <Result
                status="warning"
                title="Assessment Unavailable"
                subTitle={programBlocked}
                extra={<Button type="primary" onClick={() => navigate(`/training/${encodeURIComponent(trainingId!)}`)}>Return to Program</Button>}
            />
        </div>
    );

    if (!selectedModule && !pendingModuleId) {
        return (
            <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
                <Button icon={<LeftOutlined />} onClick={() => navigate('/')} style={{ marginBottom: '16px' }}>Back to Dashboard</Button>
                <Card style={{ marginBottom: '24px', borderRadius: '12px' }}>
                    <Title level={2}>Module Selection</Title>
                    <Text type="secondary">Select a module to begin the assessment.</Text>
                </Card>

                {/* Fullscreen Error Modal */}
                <Modal
                    open={!!fullscreenError}
                    title="Fullscreen Required"
                    onOk={() => {
                        setFullscreenError(null);
                        if (modules.length > 0) handleStartAssessment(modules[0].moduleId);
                    }}
                    onCancel={() => setFullscreenError(null)}
                    okText="Retry"
                    cancelText="Cancel"
                    centered
                >
                    <Text>{fullscreenError}</Text>
                </Modal>

                <Row gutter={[16, 16]}>
                    {modules.map((module, index) => (
                        <Col xs={24} md={12} lg={8} key={module.moduleId}>
                            <Card
                                hoverable
                                onClick={() => !requestingFullscreen && handleStartAssessment(module.moduleId)}
                                style={{ cursor: requestingFullscreen ? 'not-allowed' : 'pointer', borderRadius: '12px', height: '100%', opacity: requestingFullscreen ? 0.6 : 1 }}
                            >
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Title level={4} style={{ margin: 0 }}>Module {index + 1}</Title>
                                        {(() => {
                                            const s = (module.status || 'Not Started').toLowerCase();
                                            if (s === 'passed') return <Tag color="green">Passed</Tag>;
                                            if (s === 'failed') return <Tag color="red">Failed</Tag>;
                                            if (s === 'incomplete') return <Tag color="orange">Incomplete</Tag>;
                                            return <Tag>Not Started</Tag>;
                                        })()}
                                    </div>
                                    <Text strong>{parseModuleId(module.name).name}</Text>
                                    <Button
                                        type="primary"
                                        block
                                        style={{ backgroundColor: '#fa541c', borderColor: '#fa541c', marginTop: 16 }}
                                        loading={requestingFullscreen}
                                        disabled={requestingFullscreen}
                                    >
                                        Take Assessment
                                    </Button>
                                </Space>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    }

    if (!selectedModule && pendingModuleId) {
        return (
            <div style={{ padding: '100px 0', textAlign: 'center' }}>
                <FullscreenWarningModal
                    visible={showWarningModal}
                    onAcknowledge={handleAcknowledgeWarning}
                    onCancel={handleCancelWarning}
                />
                <Spin size="large" tip="Awaiting fullscreen acknowledgement..." />
            </div>
        );
    }

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" tip="Loading AI generated questions..." /></div>;
    }

    if (completed && result) {
        const passed = result.resultStatus === 'Passed';
        const percentage = result.percentage || 0;
        const score = result.rawScore || 0;
        const total = result.totalPoints || 0;
        const wasTerminated = result.terminationReason === 'fullscreen_violation';

        // Check if we have detailed feedback from AI grader
        const feedbackList = result.detailedAIFeedback || [];

        return (
            <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
                <Card style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    {wasTerminated ? (
                        <Result
                            status="error"
                            title="Assessment Terminated"
                            extra={[<Button type="primary" onClick={() => navigate(`/training/${encodeURIComponent(trainingId!)}`)}>Return to Program</Button>]}
                        />
                    ) : (
                        <>
                            <Result
                                status={passed ? 'success' : 'error'}
                                title={passed ? 'AI Grader: Passed!' : 'AI Grader: Not Passed'}
                                subTitle={
                                    <Space direction="vertical" size="large" style={{ marginTop: 16, width: '100%' }}>
                                        <div style={{
                                            padding: '24px',
                                            backgroundColor: passed ? '#f6ffed' : '#fff1f0',
                                            borderRadius: '12px',
                                            border: passed ? '1px solid #b7eb8f' : '1px solid #ffa39e'
                                        }}>
                                            <Row align="middle" gutter={24}>
                                                <Col span={12}>
                                                    <Progress
                                                        type="circle"
                                                        percent={percentage}
                                                        status={passed ? 'success' : 'exception'}
                                                        strokeColor={passed ? '#52c41a' : '#ff4d4f'}
                                                    />
                                                </Col>
                                                <Col span={12} style={{ textAlign: 'left' }}>
                                                    <Statistic
                                                        title="AI Percentage Score"
                                                        value={percentage}
                                                        suffix="%"
                                                        valueStyle={{ color: passed ? '#52c41a' : '#ff4d4f', fontSize: '32px' }}
                                                    />
                                                    <div style={{ marginTop: '8px' }}>
                                                        <Text type="secondary">Raw Score: </Text>
                                                        <Text strong>{score} / {total}</Text>
                                                    </div>
                                                    <div style={{ marginTop: '4px' }}>
                                                        <Text type="secondary">Passing Requirement: </Text>
                                                        <Text strong>{assessmentData?.training?.passingPercentage}%</Text>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Space>
                                }
                                extra={[
                                    <Button type="primary" key="dashboard" onClick={() => navigate(`/training/${encodeURIComponent(trainingId!)}`)} style={{ backgroundColor: '#000', borderColor: '#000' }} size="large">
                                        Return to Program
                                    </Button>
                                ]}
                            />
                        </>
                    )}
                </Card>
            </div>
        );
    }

    if (!assessmentData || questions.length === 0) {
        return (
            <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
                <Result
                    status="404"
                    title="No Assessment Questions Available"
                    extra={<Button type="primary" onClick={() => setSelectedModule(null)}>Return to Module Selection</Button>}
                />
            </div>
        );
    }

    const question = questions[currentQuestion];
    const progress = ((currentQuestion + 1) / questions.length) * 100;
    const isLastQuestion = currentQuestion === questions.length - 1;
    const currentAnswer = answers.get(question.id) || '';

    const minutes = Math.floor(timeRemaining / 60000);
    const seconds = Math.floor((timeRemaining % 60000) / 1000);
    const timeColor = timeRemaining < 300000 ? '#ff4d4f' : timeRemaining < 600000 ? '#faad14' : '#52c41a';

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', paddingBottom: '100px' }}>
            <FullscreenWarningModal visible={showWarningModal} onAcknowledge={handleAcknowledgeWarning} onCancel={handleCancelWarning} />

            {/* Instant Violation Alert Modal */}
            <Modal
                visible={!!violationReason && !completed}
                footer={null}
                closable={false}
                maskClosable={false}
                centered
                width={400}
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <WarningOutlined style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }} />
                    <Title level={4} style={{ color: '#cf1322' }}>Security Violation Detected</Title>
                    <Paragraph strong>{violationReason}</Paragraph>
                    <Paragraph>
                        Your assessment is being terminated and submitted to AI Grader.
                        Please do not close this window.
                    </Paragraph>
                    <Spin size="large" style={{ marginTop: 16 }} tip="Grading in progress..." />
                </div>
            </Modal>

            <Card style={{ marginBottom: '24px', borderRadius: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: 64, height: 64, borderRadius: '16px', backgroundColor: '#fff7e6', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 24 }}>
                            <AppstoreOutlined style={{ fontSize: '32px', color: '#fa541c' }} />
                        </div>
                        <div>
                            <Title level={3} style={{ margin: 0 }}>{assessmentData?.training?.trainingName}</Title>
                            <Text type="secondary" style={{ fontSize: '16px' }}>{assessmentData?.module?.name}</Text>
                        </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                        <Statistic title="Time Remaining" value={`${minutes}:${seconds.toString().padStart(2, '0')}`} prefix={<ClockCircleOutlined />} valueStyle={{ color: timeColor, fontSize: '24px', fontWeight: 'bold' }} />
                    </div>
                </div>
                <Divider style={{ margin: '16px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong>Question {currentQuestion + 1} of {questions.length}</Text>
                    <div style={{ width: '200px' }}><Progress percent={Math.round(progress)} showInfo={false} strokeColor="#fa541c" size="small" /></div>
                </div>
            </Card>

            <Card style={{ borderRadius: '12px', minHeight: '400px' }} bodyStyle={{ padding: '32px' }}>
                {question && <Title level={4} style={{ marginBottom: 24, userSelect: 'none' }}>{question.question}</Title>}

                {question?.codeSnippet && (
                    <Card size="small" style={{ backgroundColor: '#f5f5f5', marginBottom: 24, fontFamily: 'monospace', userSelect: 'none' }}>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{question.codeSnippet}</pre>
                    </Card>
                )}

                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <Text strong>
                            Write your answer below:
                        </Text>
                        <Space>
                            <Text type="secondary" style={{ fontSize: '12px' }}>Use Code Editor</Text>
                            <Switch 
                                size="small" 
                                checked={useCodeEditor} 
                                onChange={(checked) => !isLanguageFixed && setUseCodeEditor(checked)}
                                disabled={isLanguageFixed}
                            />
                            {useCodeEditor && (
                                <Select
                                    size="small"
                                    value={editorLanguage}
                                    style={{ width: 120 }}
                                    onChange={setEditorLanguage}
                                    disabled={isLanguageFixed}
                                    options={[
                                        { value: 'javascript', label: 'JavaScript' },
                                        { value: 'typescript', label: 'TypeScript' },
                                        { value: 'python', label: 'Python' },
                                        { value: 'cpp', label: 'C++' },
                                        { value: 'csharp', label: 'C#' },
                                        { value: 'go', label: 'Go' },
                                        { value: 'java', label: 'Java' },
                                        { value: 'sql', label: 'SQL' },
                                    ]}
                                />
                            )}
                        </Space>
                    </div>

                    {useCodeEditor ? (
                        <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
                            <div style={{ padding: '4px 12px', backgroundColor: '#fafafa', borderBottom: '1px solid #d9d9d9', fontSize: '12px', color: '#8c8c8c', display: 'flex', alignItems: 'center' }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                                    Tip: If you need to include an explanation with your code, please use code comments (e.g., // or /* */).
                                </Text>
                            </div>
                            <CodeMirror
                                value={currentAnswer}
                                height="320px"
                                theme={oneDark}
                                extensions={getLanguageExtension(editorLanguage)}
                                onChange={handleAnswerChange}
                                placeholder="// Write your code here..."
                                readOnly={!fullscreenAcknowledged}
                                editable={fullscreenAcknowledged}
                                basicSetup={{
                                    lineNumbers: true,
                                    foldGutter: true,
                                    highlightActiveLine: true,
                                }}
                            />
                        </div>
                    ) : (
                        <TextArea
                            rows={10}
                            placeholder="Type your detailed answer here... Include code snippets if necessary."
                            onChange={(e) => handleAnswerChange(e.target.value)}
                            onPaste={(e) => {
                                e.preventDefault();
                                message.warning('Pasting is disabled for assessment integrity.');
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                            value={currentAnswer}
                            disabled={!fullscreenAcknowledged}
                            style={{ fontSize: '16px', fontFamily: 'monospace' }}
                        />
                    )}
                </div>
            </Card>

            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '16px 24px', backgroundColor: '#fff', boxShadow: '0 -2px 10px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 100 }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                    <Button size="large" icon={<LeftOutlined />} onClick={handlePrevious} disabled={currentQuestion === 0 || !fullscreenAcknowledged} style={currentQuestion === 0 ? {} : { color: '#fa541c', borderColor: '#fa541c' }}>
                        Previous
                    </Button>

                    <Space>
                        {questions.map((_, index) => (
                            <div
                                key={index}
                                style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: index === currentQuestion ? '#fa541c' : answers.has(questions[index].id) && answers.get(questions[index].id)?.trim() !== '' ? '#ffd591' : '#f0f0f0', cursor: 'pointer', transition: 'all 0.3s' }}
                                onClick={() => setCurrentQuestion(index)}
                            />
                        ))}
                    </Space>

                    {isLastQuestion ? (
                        <Button type="primary" size="large" icon={<CheckOutlined />} onClick={handleSubmit} loading={submitting} disabled={!fullscreenAcknowledged} style={{ backgroundColor: '#fa541c', borderColor: '#fa541c' }}>
                            Submit For AI Grading
                        </Button>
                    ) : (
                        <Button type="primary" size="large" icon={<RightOutlined />} onClick={handleNext} disabled={!fullscreenAcknowledged} style={{ backgroundColor: '#fa541c', borderColor: '#fa541c' }}>
                            Next Question
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubjectiveAssessment;
